// The household ledger: one deterministic record per research household, shared by both presentations, the assistant
// and the facilitator. Membership, each quote, payment attempt, visit order, appointment, entry permission, work result,
// case, deal order and information request keep their own state; no state completes another.
// Every amount comes from pricing.ts. Nothing here executes a real payment, appointment, message or deletion.
import { buildQuote, selection, unitsPhrase, visitPrice, sameQuote, type Pricing, type Quote, type QuoteLine, type Reported } from './pricing.ts';
import type { Scenario } from './scenario.ts';

export type PayState = 'processing' | 'confirmed' | 'failed' | 'unknown';
export interface Payment {
  /** Stable attempt id. An unknown attempt is reconciled against this id; it is never retried. */
  id: string; for: 'join' | 'visit' | 'renewal'; quoteRef: string; version: string; amount: number; lines: string[];
  state: PayState; receipt: string | null; at: number; history: { state: PayState; at: number; note?: string }[];
}
export interface Acceptance { quoteRef: string; version: string; total: number; lines: string[]; at: number }

export type MembershipState = 'none' | 'payment_pending' | 'activation_pending' | 'active' | 'activation_problem';
export interface Membership {
  id: string; state: MembershipState; fee: number | null; paymentId: string | null; activatedAt: number | null;
  renewal: { state: 'not_offered' | 'offered' | 'consented' | 'declined'; version: string | null; amount: number | null; paymentId: string | null };
  exit: { state: 'none' | 'requested'; at: number | null };
  /** After a leave request: whether stopping future membership billing is verified, or still pending. */
  billing: 'not_applicable' | 'stop_pending' | 'stop_confirmed';
}

export type ItemResult = 'not_started' | 'performed' | 'verified' | 'partial' | 'blocked' | 'disputed';
export interface WorkItem { id: string; label: string; result: ItemResult; limitation: string | null }
export interface Remaining { item: string; owner: 'requested' | 'accepted'; action: string }
export interface Proposal {
  id: string; scope: string; complete: boolean; amount: number | null;
  decision: 'unapproved' | 'approved' | 'declined' | 'review_requested'; decidedAt: number | null;
}
export type Booking = 'not_requested' | 'requested' | 'windows_offered' | 'confirmed' | 'window_taken' | 'no_capacity' | 'change_needed';
export interface Order {
  id: string; units: 1 | 2 | 3; price: number; quoteRef: string; version: string; paymentId: string;
  preference: string | null; access: 'none' | 'granted' | 'revoked';
  booking: Booking; windows: string[]; appointment: string | null;
  assignment: 'none' | 'requested' | 'accepted' | 'unavailable';
  attendance: 'not_yet' | 'verified' | 'absent';
  items: WorkItem[]; result: 'none' | 'qualified_confirmed' | 'disputed'; remaining: Remaining[];
  finding: Proposal | null;
  /** The visit episode can close while purchased items remain open; closing it never discharges them. */
  episode: 'open' | 'closed';
  /** A reported unit change after purchase: kept as reported, for qualified scope review; the order is never silently repriced. */
  scopeNote: string | null;
}
export interface Case {
  id: string; kind: 'person' | 'visit_problem' | 'payment_reconciliation' | 'refund_review' | 'information';
  about: string | null; text: string | null; owner: 'requested' | 'accepted' | 'unavailable';
  state: 'open' | 'remedy_pending' | 'remedy_done' | 'closed' | 'reopened'; at: number; history: { at: number; what: string }[];
}
export interface DealOrder { dealId: string; total: number; state: 'authorized' | 'supplier_accepted' | 'supplier_refused'; at: number }
export interface InfoChoice { off: boolean; state: 'initial' | 'pending' | 'enforced' }
export interface Info {
  model: InfoChoice; offers: InfoChoice;
  export: 'none' | 'available' | 'failed';
  deletion: 'none' | 'partial';
  correction: { field: 'units'; from: Reported | null; to: Reported; at: number; downstream: 'pending' | 'applied' } | null;
}

export interface Household {
  units: { reported: Reported | null; history: { value: Reported; source: 'household' | 'photo_suggestion_confirmed'; at: number }[] };
  stage: 'browsing' | 'context' | 'preparing' | 'quoted' | 'declined' | 'joined';
  includeVisit: boolean;
  quote: Quote | null; issued: boolean; quoteHistory: Quote[];
  acceptance: Acceptance | null;
  payments: Payment[]; membership: Membership; orders: Order[]; cases: Case[];
  deals: { declined: string[]; orders: DealOrder[] };
  info: Info;
  decline: null | 'delete' | 'keep';
  seq: { quote: number; visit: number; attempt: number; receipt: number; case: number };
  audit: { at: number; actor: 'household' | 'staff' | 'system'; type: string; detail?: Record<string, unknown> }[];
}

/** Fictional appointment windows, offered only when the facilitator sets capacity. Never a real slot. */
export const WINDOWS = ['Tue, Oct 20 · 1–4 pm', 'Wed, Oct 21 · 1–4 pm'];
/** An optional deal for F5 research: declared synthetic amounts, not a supplier offer or a BOB price. */
export const DEAL = {
  id: 'D-7', title: 'HVAC air filters, pack of 4', supplier: '[fictional supplier]', goods: 36, delivery: 6,
  fit: 'Sized from your reported equipment · [compatibility reviewed by BOB, unresolved]',
  terms: 'Declared synthetic amounts for research · not a supplier offer or a BOB price',
};
/** F3: a qualified finding's separate proposal. Declared synthetic amount, labeled wherever shown. */
export const FINDING = { scope: 'Replace a worn condensate line on unit 1 [fictional finding]', amount: 180, label: 'Declared synthetic amount for research · not a BOB price' };

export function newHousehold(): Household {
  return {
    units: { reported: null, history: [] }, stage: 'browsing', includeVisit: true, quote: null, issued: false, quoteHistory: [],
    acceptance: null, payments: [],
    membership: { id: 'M-1042', state: 'none', fee: null, paymentId: null, activatedAt: null, renewal: { state: 'not_offered', version: null, amount: null, paymentId: null }, exit: { state: 'none', at: null }, billing: 'not_applicable' },
    orders: [], cases: [], deals: { declined: [], orders: [] },
    info: { model: { off: false, state: 'initial' }, offers: { off: false, state: 'initial' }, export: 'none', deletion: 'none', correction: null },
    decline: null, seq: { quote: 1042, visit: 1042, attempt: 0, receipt: 77310, case: 1042 }, audit: [],
  };
}

export interface Ctx { pricing: Pricing; scenario: Scenario; now: number }
export type Action =
  | { type: 'start' }
  | { type: 'report_units'; value: Reported }
  | { type: 'confirm_photo_suggestion'; suggested: number }
  | { type: 'prepare' }
  | { type: 'issue' }
  | { type: 'include_visit'; include: boolean }
  | { type: 'accept'; ref: string; version: string; total: number }
  | { type: 'request_visit' }
  | { type: 'set_preference'; orderId: string; text: string }
  | { type: 'set_access'; orderId: string; granted: boolean }
  | { type: 'request_booking'; orderId: string }
  | { type: 'choose_window'; orderId: string; window: string }
  | { type: 'change_booking'; orderId: string }
  | { type: 'report_problem'; orderId: string | null; text: string | null }
  | { type: 'dispute'; orderId: string }
  | { type: 'decide_finding'; orderId: string; decision: 'approve' | 'decline' | 'review'; amount?: number }
  | { type: 'deal_decline'; dealId: string }
  | { type: 'deal_order'; dealId: string; total: number }
  | { type: 'set_choice'; key: 'model' | 'offers'; off: boolean }
  | { type: 'request_export' }
  | { type: 'request_deletion' }
  | { type: 'renew'; version: string; amount: number }
  | { type: 'renew_decline' }
  | { type: 'leave' }
  | { type: 'refund_review' }
  | { type: 'contact'; about?: string | null }
  | { type: 'reopen_case'; caseId: string }
  | { type: 'decline_join'; retention: 'delete' | 'keep' | null };
export type StaffAction =
  | { type: 'issue' }
  | { type: 'assign'; orderId: string; state: 'accepted' | 'unavailable' }
  | { type: 'attendance'; orderId: string; state: 'verified' | 'absent' }
  | { type: 'item'; orderId: string; itemId: string; result: ItemResult; limitation?: string | null }
  | { type: 'confirm_result'; orderId: string }
  | { type: 'finding'; orderId: string; complete: boolean }
  | { type: 'handoff'; orderId: string; index: number; owner: 'accepted' }
  | { type: 'case_owner'; caseId: string; owner: 'accepted' | 'unavailable' }
  | { type: 'remedy'; caseId: string; state: 'remedy_pending' | 'remedy_done' | 'closed' }
  | { type: 'withdraw_appointment'; orderId: string }
  | { type: 'supplier_answer' }
  | { type: 'downstream_applied' };

export class LedgerError extends Error {
  code: string;
  constructor(code: string, message: string) { super(message); this.code = code; }
}
const fail = (code: string, message: string): never => { throw new LedgerError(code, message); };

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));
const audit = (h: Household, c: Ctx, actor: 'household' | 'staff' | 'system', type: string, detail?: Record<string, unknown>) =>
  h.audit.push({ at: c.now, actor, type, ...(detail ? { detail } : {}) });
const order = (h: Household, id: string) => h.orders.find((o) => o.id === id) ?? fail('no_order', `no order ${id}`);

export const isMember = (h: Household) => h.membership.state === 'active';
/** An attempt still awaiting its outcome: no new attempt may start for that quote until it is reconciled. */
export const openAttempt = (h: Household, ref: string) => h.payments.find((p) => p.quoteRef === ref && (p.state === 'processing' || p.state === 'unknown'));

function newQuote(h: Household, c: Ctx, kind: 'join' | 'visit'): Quote {
  if (kind === 'visit') h.seq.quote += 1;
  return buildQuote(c.pricing, { kind, units: h.units.reported, ref: `Q-${h.seq.quote}`, version: 'v1', terms: c.scenario.terms === 'v2' && h.issued ? 'terms v2' : 'terms v1' });
}

/** Rebuilds the open quote after a count or terms change. Once issued, any changed line or term is a new version that
 *  the household reviews again; an earlier version can no longer be accepted. */
function requote(h: Household, c: Ctx) {
  if (!h.quote || h.stage === 'joined' && h.quote.kind === 'join') return;
  const kind = h.quote.kind;
  const next = buildQuote(c.pricing, { kind, units: h.units.reported, ref: h.quote.ref, version: h.quote.version, terms: h.issued && c.scenario.terms === 'v2' ? 'terms v2' : h.quote.terms });
  if (sameQuote(next, h.quote)) return;
  if (h.issued) {
    h.quoteHistory.push(h.quote);
    next.version = `v${Number(h.quote.version.slice(1)) + 1}`;
    h.acceptance = null;
  }
  h.quote = next;
}

function newAttempt(h: Household, c: Ctx, f: Payment['for'], ref: string, version: string, amount: number, lines: string[]): Payment {
  h.seq.attempt += 1;
  const p: Payment = { id: `A-${h.seq.attempt}`, for: f, quoteRef: ref, version, amount, lines, state: c.scenario.payment, receipt: null, at: c.now, history: [{ state: c.scenario.payment, at: c.now }] };
  h.payments.push(p);
  return p;
}

const itemsFor = (units: 1 | 2 | 3): WorkItem[] =>
  Array.from({ length: units }, (_, i) => ({ id: `u${i + 1}`, label: `Unit ${i + 1} · scheduled maintenance [tasks unresolved]`, result: 'not_started', limitation: null }));

/** A verified payment confirmation and what it, and only it, makes possible. */
function confirmed(h: Household, c: Ctx, p: Payment) {
  h.seq.receipt += 1; p.receipt = `R-${h.seq.receipt - 1}`;
  if (p.for === 'renewal') { h.membership.renewal.paymentId = p.id; audit(h, c, 'system', 'renewal_paid', { attempt: p.id }); return; }
  const q = [...h.quoteHistory, h.quote].find((x) => x && x.ref === p.quoteRef && x.version === p.version) ?? h.quote!;
  if (p.for === 'join') {
    h.membership.fee = c.pricing.membership.per_year; h.membership.paymentId = p.id;
    h.membership.state = c.scenario.activation === 'active' ? 'active' : c.scenario.activation === 'pending' ? 'activation_pending' : 'activation_problem';
    if (h.membership.state === 'active') h.membership.activatedAt = c.now;
    h.stage = 'joined';
  }
  const visit = q.lines.find((l) => l.kind === 'visit');
  if (visit && p.lines.includes(visit.label) && typeof visit.units === 'number' && visit.amount !== null) {
    const id = `V-${h.seq.visit}`; h.seq.visit += 1;
    h.orders.push({ id, units: visit.units as 1 | 2 | 3, price: visit.amount, quoteRef: q.ref, version: q.version, paymentId: p.id, preference: null, access: 'none',
                    booking: 'not_requested', windows: [], appointment: null, assignment: 'none', attendance: 'not_yet', items: itemsFor(visit.units as 1 | 2 | 3),
                    result: 'none', remaining: [], finding: null, episode: 'open', scopeNote: null });
  }
  if (h.quote && h.quote.ref === q.ref) { h.quoteHistory.push(h.quote); h.quote = null; h.issued = false; h.acceptance = null; }
}

/** Applies the facilitator's current outcomes to records still waiting on them: an attempt still processing or unknown
 *  takes a definitive result only when one is set (late success is still one charge); a pending activation follows the
 *  activation outcome; the renewal offer appears or not. Called after every action and every scenario change. */
export function reconcile(h0: Household, c: Ctx): Household {
  const h = clone(h0);
  for (const p of h.payments) {
    if (p.state !== 'processing' && p.state !== 'unknown') continue;
    const s = c.scenario.payment;
    if (s === p.state) continue;
    if (s === 'processing' && p.state === 'unknown') continue; // an unknown result never goes back to "processing"
    p.history.push({ state: s, at: c.now, note: p.state === 'unknown' ? 'reconciled against the original attempt' : undefined });
    p.state = s;
    if (s === 'confirmed') confirmed(h, c, p);
    if (p.for === 'join' && (s === 'processing' || s === 'unknown')) h.membership.state = 'payment_pending';
    if (p.for === 'join' && s === 'failed' && h.membership.state === 'payment_pending') h.membership.state = 'none';
    audit(h, c, 'system', 'payment_outcome', { attempt: p.id, state: s });
  }
  const m = h.membership;
  if (m.state === 'activation_pending' || m.state === 'activation_problem') {
    const next: MembershipState = c.scenario.activation === 'active' ? 'active' : c.scenario.activation === 'pending' ? 'activation_pending' : 'activation_problem';
    if (next !== m.state) { m.state = next; if (next === 'active') m.activatedAt = c.now; audit(h, c, 'system', 'activation', { state: next }); }
  }
  if (m.state === 'active' && m.renewal.state === 'not_offered' && c.scenario.renewal === 'offered') m.renewal = { state: 'offered', version: 'next term T2', amount: c.pricing.membership.per_year, paymentId: null };
  if (m.renewal.state === 'offered' && c.scenario.renewal === 'not_offered') m.renewal = { state: 'not_offered', version: null, amount: null, paymentId: null };
  if (m.exit.state === 'requested') m.billing = c.scenario.billing;
  for (const k of ['model', 'offers'] as const) if (h.info[k].state === 'pending' && c.scenario.info === 'enforced') h.info[k].state = 'enforced';
  requote(h, c);
  return h;
}

/** Applies one household action. Throws LedgerError when the action isn't permitted in the current state. */
export function act(h0: Household, a: Action, c: Ctx): Household {
  const h = clone(h0);
  switch (a.type) {
    case 'start': if (h.stage === 'browsing') h.stage = 'context'; break;
    case 'report_units': {
      const before = h.units.reported;
      h.units.reported = a.value; h.units.history.push({ value: a.value, source: 'household', at: c.now });
      for (const o of h.orders) {
        if (o.episode === 'open' && o.units !== a.value) o.scopeNote = `You reported ${unitsPhrase(a.value)} after this visit was purchased for ${unitsPhrase(o.units)}. BOB reviews the scope; the accepted price is not changed silently.`;
        if (o.units === a.value) o.scopeNote = null;
      }
      if (before !== null && before !== a.value && (h.stage === 'joined' || h.orders.length)) h.info.correction = { field: 'units', from: before, to: a.value, at: c.now, downstream: 'pending' };
      requote(h, c); break;
    }
    case 'confirm_photo_suggestion':
      if (![1, 2, 3].includes(a.suggested) && a.suggested < 4) fail('bad_count', 'not a count');
      { const v: Reported = a.suggested >= 4 ? 'four_plus' : (a.suggested as 1 | 2 | 3);
        h.units.reported = v; h.units.history.push({ value: v, source: 'photo_suggestion_confirmed', at: c.now }); requote(h, c); }
      break;
    case 'prepare':
      if (c.scenario.area === 'not_served') fail('not_served', 'this address is not served');
      if (c.scenario.intake === 'restricted' && !isMember(h)) fail('intake_restricted', 'new joining is paused');
      h.stage = 'preparing'; if (!h.quote) h.quote = newQuote(h, c, 'join'); else requote(h, c); break;
    case 'issue':
      if (!h.quote) h.quote = newQuote(h, c, isMember(h) ? 'visit' : 'join');
      if (!h.issued) { h.issued = true; h.stage = h.stage === 'joined' ? 'joined' : 'quoted'; audit(h, c, 'system', 'quote_issued', { ref: h.quote.ref, version: h.quote.version }); }
      requote(h, c); break;
    case 'include_visit': h.includeVisit = a.include; if (h.acceptance) h.acceptance = null; break;
    case 'accept': {
      const q = h.quote; if (!q || !h.issued) fail('no_quote', 'there is no issued quote to accept');
      if (q!.ref !== a.ref || q!.version !== a.version) fail('stale_version', `${a.ref} ${a.version} is not the current quote; review ${q!.ref} ${q!.version}`);
      const sel = selection(q!, h.includeVisit);
      if (sel.total === null) fail('unpriced', 'a chosen line needs a scoped quote before it can be authorized');
      if (sel.total !== a.total) fail('amount_mismatch', `the reviewed total does not match the quote (${sel.total})`);
      const open = openAttempt(h, q!.ref);
      if (open) fail('attempt_open', `attempt ${open.id} is ${open.state}; it is reconciled before any new attempt`);
      if (h.payments.some((p) => p.quoteRef === q!.ref && p.version === q!.version && p.state === 'confirmed')) fail('already_paid', 'this quote is already paid');
      if (q!.kind === 'visit' && !isMember(h)) fail('not_member', 'member visit prices need an active membership');
      if (q!.kind === 'join' && c.scenario.intake === 'restricted') fail('intake_restricted', 'new joining is paused');
      h.acceptance = { quoteRef: q!.ref, version: q!.version, total: sel.total!, lines: sel.lines.map((l) => l.label), at: c.now };
      const p = newAttempt(h, c, q!.kind, q!.ref, q!.version, sel.total!, sel.lines.map((l) => l.label));
      audit(h, c, 'household', 'accept', { ref: q!.ref, version: q!.version, total: sel.total, attempt: p.id });
      if (p.state === 'confirmed') confirmed(h, c, p);
      else if (q!.kind === 'join' && p.state !== 'failed') h.membership.state = 'payment_pending';
      if (p.state === 'unknown') openCase(h, c, 'payment_reconciliation', p.id, null, 'requested');
      break;
    }
    case 'request_visit':
      if (!isMember(h)) fail('not_member', 'member visit prices need an active membership');
      if (h.quote) h.quoteHistory.push(h.quote);
      h.seq.quote += 1;
      h.quote = buildQuote(c.pricing, { kind: 'visit', units: h.units.reported, ref: `Q-${h.seq.quote}`, version: 'v1', terms: 'terms v1' });
      h.issued = true; h.acceptance = null; audit(h, c, 'household', 'request_visit', { ref: h.quote.ref }); break;
    case 'set_preference': order(h, a.orderId).preference = a.text.slice(0, 120) || null; break;
    case 'set_access': order(h, a.orderId).access = a.granted ? 'granted' : order(h, a.orderId).access === 'none' ? 'none' : 'revoked'; break;
    case 'request_booking': case 'change_booking': {
      const o = order(h, a.orderId);
      if (o.attendance !== 'not_yet') fail('visit_done', 'this visit has already been attended');
      if (a.type === 'change_booking' && o.booking !== 'confirmed') fail('nothing_to_change', 'no confirmed appointment to change');
      o.appointment = a.type === 'change_booking' ? o.appointment : null;
      if (c.scenario.capacity === 'windows') { o.booking = 'windows_offered'; o.windows = [...WINDOWS]; } else { o.booking = 'no_capacity'; o.windows = []; }
      if (o.assignment === 'none') o.assignment = 'requested';
      break;
    }
    case 'choose_window': {
      const o = order(h, a.orderId);
      if (!o.windows.includes(a.window)) fail('no_window', 'that window is not offered');
      if (c.scenario.booking === 'confirm') { o.booking = 'confirmed'; o.appointment = a.window; o.windows = []; }
      else { o.booking = 'window_taken'; o.windows = o.windows.filter((w) => w !== a.window); }
      break;
    }
    case 'report_problem': openCase(h, c, 'visit_problem', a.orderId, a.text, c.scenario.person); break;
    case 'dispute': { const o = order(h, a.orderId); o.result = 'disputed'; openCase(h, c, 'visit_problem', o.id, 'The household disagrees with the recorded result.', c.scenario.person); break; }
    case 'decide_finding': {
      const o = order(h, a.orderId); const f = o.finding ?? fail('no_finding', 'no finding on this visit');
      if (f!.decision === 'approved') fail('decided', 'already approved');
      if (a.decision === 'approve') {
        if (!f!.complete || f!.amount === null) fail('incomplete_proposal', 'an incomplete proposal cannot be approved');
        if (a.amount !== f!.amount) fail('amount_mismatch', 'the reviewed amount does not match the proposal');
        f!.decision = 'approved';
      } else f!.decision = a.decision === 'decline' ? 'declined' : 'review_requested';
      f!.decidedAt = c.now; audit(h, c, 'household', 'finding_decision', { order: o.id, decision: f!.decision }); break;
    }
    case 'deal_decline': if (!h.deals.declined.includes(a.dealId)) h.deals.declined.push(a.dealId); break;
    case 'deal_order': {
      if (!isMember(h)) fail('not_member', 'deals are for members');
      if (c.scenario.deals !== 'available' || a.dealId !== DEAL.id) fail('no_deal', 'that deal is not available');
      if (a.total !== DEAL.goods + DEAL.delivery) fail('amount_mismatch', 'the reviewed total does not match the deal');
      if (h.deals.orders.some((d) => d.dealId === a.dealId && d.state === 'authorized')) fail('attempt_open', 'this order is already waiting for the supplier');
      h.deals.orders.push({ dealId: a.dealId, total: a.total, state: 'authorized', at: c.now }); break;
    }
    case 'set_choice': h.info[a.key] = { off: a.off, state: c.scenario.info === 'enforced' ? 'enforced' : 'pending' }; break;
    case 'request_export': h.info.export = c.scenario.export; break;
    case 'request_deletion': h.info.deletion = 'partial'; break;
    case 'renew': {
      const r = h.membership.renewal;
      if (r.state !== 'offered') fail('no_offer', 'there is no next-term offer to approve');
      if (a.version !== r.version || a.amount !== r.amount) fail('stale_version', 'the next-term offer changed; review it again');
      if (h.payments.some((p) => p.for === 'renewal' && (p.state === 'processing' || p.state === 'unknown'))) fail('attempt_open', 'a renewal attempt is being reconciled');
      r.state = 'consented';
      const p = newAttempt(h, c, 'renewal', r.version!, 'next term', r.amount!, ['Club membership, next term']);
      if (p.state === 'confirmed') confirmed(h, c, p);
      if (p.state === 'unknown') openCase(h, c, 'payment_reconciliation', p.id, null, 'requested');
      break;
    }
    case 'renew_decline': h.membership.renewal = { ...h.membership.renewal, state: 'declined' }; break;
    case 'leave':
      if (h.membership.state === 'none') fail('not_member', 'no membership to leave');
      h.membership.exit = { state: 'requested', at: c.now }; h.membership.billing = c.scenario.billing;
      if (h.membership.renewal.state === 'offered') h.membership.renewal.state = 'declined';
      audit(h, c, 'household', 'leave', { open: openCommitments(h).length }); break;
    case 'refund_review': openCase(h, c, 'refund_review', h.membership.id, 'Owned review of any money after leaving. No club refund rule is approved.', 'requested'); break;
    case 'contact': {
      const existing = h.cases.find((x) => x.kind === 'person' && x.state !== 'closed');
      if (!existing) openCase(h, c, 'person', a.about ?? null, null, c.scenario.person);
      break;
    }
    case 'reopen_case': { const k = h.cases.find((x) => x.id === a.caseId) ?? fail('no_case', 'no such case'); k!.state = 'reopened'; k!.history.push({ at: c.now, what: 'reopened by the household' }); break; }
    case 'decline_join': h.stage = 'declined'; h.decline = a.retention; break;
  }
  if (a.type !== 'start') audit(h, c, 'household', a.type);
  return reconcile(h, c);
}

function openCase(h: Household, c: Ctx, kind: Case['kind'], about: string | null, text: string | null, owner: Case['owner']) {
  const dup = h.cases.find((x) => x.kind === kind && x.about === about && x.state !== 'closed');
  if (dup) { dup.history.push({ at: c.now, what: 'the same issue was reported again; linked, no new clock' }); return dup; }
  h.seq.case += 1;
  const k: Case = { id: `C-${h.seq.case - 1}`, kind, about, text, owner, state: 'open', at: c.now, history: [{ at: c.now, what: 'received' }] };
  h.cases.push(k); return k;
}

/** Staff actions from the facilitator panel: the technician's and service lead's own records (F8, F9). */
export function staff(h0: Household, a: StaffAction, c: Ctx): Household {
  let h = clone(h0);
  switch (a.type) {
    case 'issue': h = act(h, { type: 'issue' }, c); break;
    case 'assign': order(h, a.orderId).assignment = a.state; break;
    case 'attendance': {
      const o = order(h, a.orderId);
      if (o.booking !== 'confirmed') fail('no_appointment', 'attendance needs a confirmed appointment');
      o.attendance = a.state;
      if (a.state === 'absent') openCase(h, c, 'visit_problem', o.id, 'BOB detected no attendance evidence for the confirmed appointment.', 'requested');
      break;
    }
    case 'item': {
      const o = order(h, a.orderId); const it = o.items.find((i) => i.id === a.itemId) ?? fail('no_item', 'no such item');
      if (o.attendance !== 'verified' && a.result !== 'blocked') fail('no_attendance', 'work results need verified attendance');
      it!.result = a.result; it!.limitation = a.limitation ?? (a.result === 'partial' || a.result === 'blocked' ? '[stated limitation]' : null); break;
    }
    case 'confirm_result': {
      const o = order(h, a.orderId);
      if (o.items.some((i) => i.result === 'not_started')) fail('items_open', 'every item needs a recorded result before qualified confirmation');
      o.result = 'qualified_confirmed'; o.episode = 'closed';
      o.remaining = o.items.filter((i) => i.result === 'partial' || i.result === 'blocked').map((i) => ({ item: i.label, owner: 'requested', action: 'Arrange completion of this purchased item at BOB\'s cost [next arrangement]' }));
      break;
    }
    case 'finding': order(h, a.orderId).finding = { id: 'P-1', scope: FINDING.scope, complete: a.complete, amount: a.complete ? FINDING.amount : null, decision: 'unapproved', decidedAt: null }; break;
    case 'handoff': { const r = order(h, a.orderId).remaining[a.index] ?? fail('no_item', 'no such remaining item'); r!.owner = a.owner; break; }
    case 'case_owner': { const k = h.cases.find((x) => x.id === a.caseId) ?? fail('no_case', 'no such case'); k!.owner = a.owner; k!.history.push({ at: c.now, what: `owner ${a.owner}` }); break; }
    case 'remedy': {
      const k = h.cases.find((x) => x.id === a.caseId) ?? fail('no_case', 'no such case');
      if (a.state !== 'remedy_pending' && k!.owner !== 'accepted') fail('no_owner', 'a remedy needs an accepted owner');
      k!.state = a.state; k!.history.push({ at: c.now, what: a.state }); break;
    }
    case 'withdraw_appointment': { const o = order(h, a.orderId); if (o.booking === 'confirmed') { o.booking = 'change_needed'; o.windows = []; } break; }
    case 'supplier_answer': for (const d of h.deals.orders) if (d.state === 'authorized') d.state = c.scenario.supplier === 'accepts' ? 'supplier_accepted' : 'supplier_refused'; break;
    case 'downstream_applied': if (h.info.correction) h.info.correction.downstream = 'applied'; break;
  }
  audit(h, c, 'staff', a.type);
  return reconcile(h, c);
}

/** What stays owed whatever happens to membership: unfinished purchased visits, open remaining items, open cases,
 *  undecided deal orders and payments still being reconciled. Leaving never removes any of these. */
export function openCommitments(h: Household): { kind: string; id: string; text: string }[] {
  const out: { kind: string; id: string; text: string }[] = [];
  for (const o of h.orders) {
    if (o.result === 'none') out.push({ kind: 'visit', id: o.id, text: `${o.id}: purchased visit, ${unitsPhrase(o.units)}, not yet completed` });
    for (const r of o.remaining) out.push({ kind: 'remaining', id: o.id, text: `${o.id}: ${r.item} · owner ${r.owner === 'accepted' ? 'accepted' : 'requested, not yet accepted'}` });
  }
  for (const k of h.cases) if (k.state !== 'closed' && k.state !== 'remedy_done') out.push({ kind: 'case', id: k.id, text: `${k.id}: open case` });
  for (const d of h.deals.orders) if (d.state === 'authorized') out.push({ kind: 'deal', id: d.dealId, text: `${d.dealId}: deal order waiting for the supplier` });
  for (const p of h.payments) if (p.state === 'processing' || p.state === 'unknown') out.push({ kind: 'payment', id: p.id, text: `${p.id}: payment ${p.state}, being reconciled` });
  return out;
}

/** The current purchase the household is looking at, with exactly the lines chosen and their total. */
export function purchase(h: Household): { quote: Quote; lines: QuoteLine[]; total: number | null } | null {
  if (!h.quote) return null;
  return { quote: h.quote, ...selection(h.quote, h.includeVisit) };
}

export { visitPrice };
